import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Grid, Type } from '../../app/StyleGuide';
import { ContainerFluid, Container } from '../../components/Layout';
import { CompilationBanner } from '../manageCatalog/components/CompilationElements';
import TrainingCard from '../training/components/TrainingCard';
import TrainingListItem from './TrainingListItem';
import { BreadcrumbsSubMenu, MobileCrumb } from '../../components/Breadcrumbs';
import LoadingState from '../../components/LoadingState';
import ErrorMessage from '../../components/ErrorMessage';
import HelpAndInfoBox from '../../components/HelpAndInfoBox';
import windowService from '../../services/windowService';
import compilationService from '../../services/compilationService';
import { TrainingItem } from './models/trainingItem';
import { getCompilation } from './api/getCompilation';

const CompilationInformation = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const Name = styled.h1`
  margin: ${Grid._3} 0;
  color: ${Type.Color.dark};
  font-size: ${Type.Scale._6};
  font-weight: ${Type.Weight.bold};
  line-height: ${Type.Leading.normal};
`;

const Description = styled.p`
  color: ${Type.Color.mediumDark};
  font-size: ${Type.Scale._4};
  line-height: ${Type.Leading.tall};
  margin: ${Grid._3} 0;
`;

export function TrainingCompilation() {
  const [crumbs, setCrumbs] = useState([{ name: 'Catalog', route: '#/catalog' }, { name: '' }]);
  const [data, setData] = useState<{
    compilation?: {
      trainingItems: TrainingItem[];
      image?: string | null;
      name?: string;
      longDescription?: string;
      trainingItemsDisplayStyle?: string;
    };
    isLoading: boolean;
    isError?: boolean;
  }>({
    compilation: { trainingItems: [] },
    isLoading: true,
  });

  const compilationId = windowService.getRoutePart(3);

  const redirectToTraining = (id: string) => windowService.redirectTo(`#/catalog/compilation/${compilationId}/${id}`);

  useEffect(() => {
    getCompilation(compilationId)
      .then(compilation => {
        setData({ compilation: compilation, isLoading: false });
        setCrumbs([{ name: 'Catalog', route: '#/catalog' }, { name: `${compilation.name}` }]);
      })
      .catch(err => {
        console.error(err);
        setData({ isError: true, isLoading: false });
      });
  }, []);

  return (
    <>
      <div className="hidden-xs hidden-sm">
        <BreadcrumbsSubMenu crumbs={crumbs} />
      </div>
      {data.isLoading ? (
        <Container>
          <LoadingState />
        </Container>
      ) : data.isError ? (
        <Container>
          <ErrorMessage>
            A problem occurred showing this page. Please refresh the page to try again. <a href="#/help">Contact Us</a>
          </ErrorMessage>
        </Container>
      ) : (
        <>
          {data.compilation?.image && (
            <ContainerFluid>
              {data.compilation.image && (
                <CompilationBanner
                  src={compilationService.getCompilationImageUrl(data.compilation.image)}
                  alt={data.compilation.name}
                />
              )}
            </ContainerFluid>
          )}
          <Container className="visible-xs-block visible-sm-block" style={{ margin: `${Grid._4} 0 -${Grid._4} 0` }}>
            <MobileCrumb crumbs={crumbs} />
          </Container>
          <Container data-qa-hook="compilationView" style={{ margin: `${Grid._6} auto` }}>
            <CompilationInformation>
              <Name data-qa-hook="compilationName">{data.compilation?.name}</Name>
              <Description data-qa-hook="compilationDescription">{data.compilation?.longDescription}</Description>
            </CompilationInformation>
          </Container>
          {data.compilation?.trainingItems.length && data.compilation?.trainingItemsDisplayStyle === 'Grid' ? (
            <Container
              className="grid-container grid-col-2 grid-sm-col-6 grid-gap-24"
              style={{ margin: `${Grid._6} auto` }}
            >
              {data.compilation.trainingItems.map(training => (
                <div key={training.id} className="grid-col-span-2 grid-sm-col-span-3 grid-md-col-span-2">
                  <TrainingCard training={training} redirectToTraining={() => redirectToTraining(training.id)} />
                </div>
              ))}
            </Container>
          ) : data.compilation?.trainingItems.length && data.compilation?.trainingItemsDisplayStyle === 'List' ? (
            <Container style={{ margin: `${Grid._6} auto` }}>
              {data.compilation?.trainingItems.map(training => (
                <TrainingListItem key={training.id} training={training} redirectToTraining={redirectToTraining} />
              ))}
            </Container>
          ) : (
            <Container>
              <HelpAndInfoBox
                title="Don't worry, training is on the way"
                description="Authors are curating content for this compilation and it will be visible here soon."
              />
            </Container>
          )}
        </>
      )}
    </>
  );
}
